import { useState } from "react";
import axios from "axios";
import { API } from "../global/global";
import { toast } from "react-toastify";

interface UploadPDFHook {
  uploadPDF: (
    evaluadoUuid: string,
    folderName: string,
    file: File | null
  ) => Promise<{ success: boolean; message: string }>;
  deletePDF: (
    evaluadoUuid: string,
    folderName: string,
    fileName: string
  ) => Promise<{ success: boolean; message: any }>;
  handleFile: (
    evaluadoUuid: string,
    folderName: string,
    fileName: string,
    action: "view" | "download"
  ) => Promise<void>;
  handleMergePdfs: (
    evaluadoUuid: string,
    folderName: string,
    fileName: string,
    action: "view" | "download"
  ) => Promise<void>;
  fetchFilesData: (
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setFiles: React.Dispatch<
      React.SetStateAction<Record<string, string | File>>
    >,
    evaluadosList_id: string,
    maxPrefix: number,
    folderName: string
  ) => Promise<void>;
  listarPlantillas: (carpeta: string) => Promise<any[]>;
  descargarPlantillas: (
    carpeta: string,
    selectedTemplates: string[]
  ) => Promise<void>;
}

const usePdfPresencial = (): UploadPDFHook => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<any | null>(null);

  const token = localStorage.getItem("token");

  const uploadPDF = async (
    evaluadoUuid: string,
    folderName: string,
    file: File | null
  ): Promise<{ success: boolean; message: string }> => {
    if (!file) {
      const errorMessage = "No se seleccionó ningún archivo.";
      setError(errorMessage);
      toast.error(errorMessage); // Mostrar error con react-toastify
      return { success: false, message: errorMessage };
    }

    const formData = new FormData();
    formData.append("pdf", file);

    const url = `${API}/evaluado/${evaluadoUuid}/upload-pdf/${folderName}?timestamp=${Date.now()}`;

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          // "Cache-Control": "no-cache",
          Authorization: `Bearer ${token}`,
        },
      });

      const successMessage = response.data?.message;
      setSuccess(successMessage);
      toast.success(successMessage); // Mostrar éxito con react-toastify
      return { success: true, message: successMessage };
    } catch (err) {
      let errorMessage = "Error al subir el archivo.";

      if (axios.isAxiosError(err)) {
        errorMessage = err.response?.data?.message || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage); // Mostrar error con react-toastify
      console.error("Error al subir el archivo:", err);

      return { success: false, message: errorMessage };
    } finally {
      setUploading(false);
    }
  };

  const deletePDF = async (
    evaluadoUuid: string,
    folderName: string,
    fileName: string
  ) => {
    const url = `${API}/evaluados/pdfs/${evaluadoUuid}/${folderName}/${fileName}?timestamp=${Date.now()}`;

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const response = await axios.delete(url, {
        headers: {
          // "Cache-Control": "no-cache",
          Authorization: `Bearer ${token}`,
        },
      });

      const successMessage = response.data?.message;
      setSuccess(successMessage); // Actualiza el estado de éxito
      toast.success(successMessage); // Mostrar éxito con react-toastify

      return { success: true, message: successMessage };
    } catch (err) {
      let errorMessage = "Error al eliminar el archivo.";

      if (axios.isAxiosError(err)) {
        errorMessage = err.response?.data?.message || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage); // Establecer el mensaje de error
      toast.error(errorMessage); // Mostrar error con react-toastify
      console.error("Error al eliminar el archivo:", err);

      return { success: false, message: errorMessage };
    } finally {
      setUploading(false); // Restablecer el estado de carga
    }
  };

  const handleFile = async (
    evaluadoUuid: string,
    folderName: string,
    fileName: string,
    action: "view" | "download"
  ): Promise<void> => {
    // No se devuelve nada
    const fileUrl = `${API}/evaluados/pdfs/${evaluadoUuid}/${folderName}/${fileName}/${action}?timestamp=${Date.now()}`;
    console.log("URL de la API:", fileUrl);

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const response = await axios.get(fileUrl, {
        headers: {
          // "Cache-Control": "no-cache",
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob", // Esto es importante para manejar archivos binarios (como PDF)
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);

      if (action === "view") {
        window.open(url, "_blank");
      } else if (action === "download") {
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
      }

      const successMessage = `PDF ${
        action === "view" ? "abierto" : "descargado"
      } exitosamente.`;
      setSuccess(successMessage); // Actualiza el estado de éxito
      toast.success(successMessage); // Mostrar éxito con react-toastify
    } catch (error: unknown) {
      let errorMessage = "Hubo un error al intentar obtener el archivo.";

      // Comprobamos si es un error de Axios y manejamos la respuesta adecuada
      if (axios.isAxiosError(error)) {
        // Si es un error 403, indicamos específicamente que no se tiene autorización
        if (error.response?.status === 403) {
          errorMessage = "No tienes autorización para acceder a este archivo.";
        } else {
          errorMessage = error.response?.data?.message || errorMessage;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setError(errorMessage); // Establecer el mensaje de error
      toast.error(errorMessage); // Mostrar error con react-toastify
      console.error(errorMessage, error);
    } finally {
      setUploading(false); // Restablecer el estado de carga
    }
  };

  const handleMergePdfs = async (
    evaluadoUuid: string,
    folderName: string,
    fileName: string,
    action: "view" | "download"
  ) => {
    const url = `${API}/evaluados/pdfs/${evaluadoUuid}/merge-pdfs/${folderName}/${action}/${fileName}?timestamp=${Date.now()}`;
    console.log("URL de la API:", url);

    try {
      const response = await axios.post(url, null, {
        headers: {
          // "Cache-Control": "no-cache",
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob", // Esto es importante para manejar archivos binarios (como PDF)
      });

      // Si la respuesta es exitosa (status 2xx)
      const blob = response.data;
      const urlBlob = window.URL.createObjectURL(blob);

      if (action === "view") {
        window.open(urlBlob, "_blank");
      } else if (action === "download") {
        const a = document.createElement("a");
        a.href = urlBlob;
        a.download = fileName;
        a.click();
      }

      toast.success(
        `PDF ${action === "view" ? "abierto" : "descargado"} exitosamente.`
      );
    } catch (error) {
      // Verificar si el error es una instancia de AxiosError
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Manejar error 403
          if (error.response.status === 403) {
            const errorMessage =
              "No tienes autorización para acceder a este archivo.";
            toast.error(errorMessage);
            console.error(errorMessage);
          } else {
            // Extraer el mensaje de error del cuerpo de la respuesta en caso de error distinto
            const errorMessage =
              error.response?.data?.message ||
              "Hubo un problema desconocido al combinar los PDFs.";
            toast.error(errorMessage);
            console.error(errorMessage);
          }
        } else {
          const errorMessage = "Hubo un error en la conexión o la solicitud.";
          toast.error(errorMessage);
          console.error(errorMessage, error);
        }
      } else {
        // Si el error no es un AxiosError, lo manejamos de otra forma
        const errorMessage = "Error al combinar los PDFs.";
        toast.error(errorMessage);
        console.error(errorMessage, error);
      }
    }
  };

  const fetchFilesData = async (
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setFiles: React.Dispatch<
      React.SetStateAction<Record<string, string | File>>
    >,
    evaluadosList_id: string,
    maxPrefix: number, // Número máximo de prefijos que se deben manejar
    carpeta: string
  ) => {
    setIsLoading(true);

    try {
      const response = await axios.get(
        `${API}/evaluado/${evaluadosList_id}/pdf/listFiles/${carpeta}`,
        {
          params: { timestamp: Date.now() },
          headers: {
            "Content-Type": "application/json",
            // "Cache-Control": "no-cache",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { files } = response.data;

      setFiles((prevFiles) => {
        const updatedFiles = { ...prevFiles };
        let hasChanged = false;

        files.forEach((file: string | File) => {
          const fileName = typeof file === "string" ? file : file.name;

          // Genera dinámicamente el fileKey basado en los prefijos y el parámetro maxPrefix
          let fileKey = "file1"; // Valor predeterminado si no se encuentra ningún prefijo válido

          for (let i = 1; i <= maxPrefix; i++) {
            if (fileName.startsWith(`${String(i).padStart(2, "0")}_`)) {
              fileKey = `file${i}`;
              break;
            }
          }

          if (updatedFiles[fileKey] !== file) {
            updatedFiles[fileKey] = file;
            hasChanged = true;
          }
        });

        return hasChanged ? updatedFiles : prevFiles;
      });
    } catch (error) {
      console.error("Error al obtener los archivos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const listarPlantillas = async (carpeta: string): Promise<any[]> => {
    // Cambia 'void' a 'any[]' para devolver un arreglo
    try {
      const response = await axios.get(`${API}/plantillas/listar/${carpeta}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`, // Token de autorización
        },
      });

      const { archivos } = response.data;

      if (Array.isArray(archivos) && archivos.length > 0) {
        return archivos; // Devuelve las plantillas obtenidas
      } else {
        toast.info("No se encontraron plantillas en esta carpeta.");
        return []; // Devuelve un arreglo vacío si no se encuentran plantillas
      }
    } catch (error: any) {
      console.error("Error al listar las plantillas:", error);

      if (error.response?.status === 401) {
        toast.error("No estás autenticado. Por favor, inicia sesión.");
      } else if (error.response?.status === 403) {
        toast.error("No tienes autorización para acceder a estas plantillas.");
      } else {
        toast.error("Hubo un problema al listar las plantillas.");
      }
      return []; // Devuelve un arreglo vacío en caso de error
    }
  };

  const descargarPlantillas = async (
    carpeta: string,
    selectedTemplates: string[]
  ): Promise<void> => {
    try {
      const response = await axios.post(
        `${API}/plantillas/descargar/${carpeta}`,
        { archivos: selectedTemplates },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob", // Asegúrate de que el backend devuelve un blob
        }
      );

      // Crear URL para el archivo descargado
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      if (selectedTemplates.length === 1) {
        // Si solo se selecciona una plantilla
        link.setAttribute("download", selectedTemplates[0]);
      } else {
        // Si se seleccionan varias plantillas, lo descargamos como un ZIP
        link.setAttribute("download", `plantillas_${carpeta}.zip`);
      }
      document.body.appendChild(link);
      link.href = url;
      link.click();
      link.remove();

      // Mensaje de éxito
      if (selectedTemplates.length === 1) {
        toast.success("Plantilla descargada con éxito.");
      } else {
        toast.success("Plantillas descargadas con éxito.");
      }
    } catch (error: any) {
      console.error("Error al descargar los archivos:", error);
      toast.error("Hubo un problema al intentar descargar las plantillas.");
    }
  };

  return {
    uploadPDF,
    deletePDF,
    handleFile,
    handleMergePdfs,
    fetchFilesData,
    listarPlantillas,
    descargarPlantillas,
  };
};

export default usePdfPresencial;
